/**
 * Whether there is any AI to ask - so the bulbs can leave a server that has none alone.
 *
 * Two things make it false, and both are plain facts about the server: it was started with AI
 * switched off (`EFSS_AI_ENABLED`, reported by `/api/health`), or no model is assigned to any tier
 * yet, which is what a fresh install looks like. Either way a bulb would only ever be refused, and
 * a control that cannot do anything is better absent.
 *
 * With no answer, the last one this device was given stands (kept in localStorage): a server runs
 * with AI or without it and that does not change from day to day, so being offline or a check
 * failing must not take the bulbs away from someone who has AI. Only a device that has never had
 * an answer starts from "no" (the review of #91): until the server has said there is an AI, the
 * bulbs, and the Add box's Auto and Context chips, are not offered, rather than offered and then
 * refused. A failed answer is not kept, so the next control to appear asks again.
 *
 * Asked once per page load and shared by every bulb: a server's `EFSS_AI_ENABLED` does not change
 * under it, so there is nothing to keep re-checking, and an Inbox full of rows would otherwise ask
 * once each. A reload asks again. Two things shorten that: an answer that failed is not kept, so the
 * next bulb to appear asks afresh, and assigning models on the AI page forgets it, since that is how
 * a fresh server gets AI (`resetAiConfigured`).
 */

import { useEffect, useState } from "react";
import { api } from "./api";

/** `enabled`: the server runs AI at all. `configured`: and a model is assigned to a tier. A server
 * with AI off has nothing to read Context into either, which is why the Add box drops that chip
 * for `enabled` false but keeps it for an account that merely has no model yet. */
type Answer = { enabled: boolean; configured: boolean };

const NONE: Answer = { enabled: false, configured: false };

const KEY = "efss.aiAnswer";

let cached: Promise<Answer> | null = null;

/** The last answer this device was given, so a control starts from it instead of from "no", and
 * an answer that does not arrive leaves it as it was. */
function lastKnown(): Answer | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<Answer>) : null;
    return parsed && typeof parsed.enabled === "boolean" && typeof parsed.configured === "boolean"
      ? { enabled: parsed.enabled, configured: parsed.configured }
      : null;
  } catch {
    return null;
  }
}

function remember(answer: Answer): Answer {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(answer));
  } catch {
    /* storage blocked: the answer still holds for this page */
  }
  return answer;
}

async function lookup(): Promise<Answer> {
  try {
    const [health, slots] = await Promise.all([api.health(), api.modelSlots()]);
    return remember(
      health.ai_enabled
        ? {
            enabled: true,
            configured: Object.values(slots).some((slot) => slot !== null && slot !== undefined),
          }
        : NONE,
    );
  } catch {
    cached = null; // no answer is not an answer: ask again next time
    return lastKnown() ?? NONE;
  }
}

function aiAnswer(): Promise<Answer> {
  cached ??= lookup();
  return cached;
}

/** Forget the answer: after models are assigned, and in tests. */
export function resetAiConfigured(): void {
  cached = null;
}

function useAnswer(): Answer {
  const [answer, setAnswer] = useState<Answer>(() => lastKnown() ?? NONE);
  useEffect(() => {
    let live = true;
    void aiAnswer().then((next) => live && setAnswer(next));
    return () => {
      live = false;
    };
  }, []);
  return answer;
}

/** `true` once the server has said there is an AI to ask. */
export function useAiConfigured(): boolean {
  return useAnswer().configured;
}

/** `true` once the server has said it runs AI at all (`EFSS_AI_ENABLED`). */
export function useAiServerEnabled(): boolean {
  return useAnswer().enabled;
}
